import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {Alert} from '../components/Ui/Alert.jsx';
import { register } from '../http/auth.js';
import logo from '../assets/logo.png';
import TextInputWithLabel from '../components/Inputs/TextInput.jsx';
import { FieldContainer, Row } from '../components/StyledComponents/Forms/Forms.jsx';
import SelectWithLabel from '../components/Inputs/Select.jsx';

const LoginContainer = styled.div`
  max-width: 600px;
  margin: 100px auto;
  padding: 20px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  background: #fff;
`;

const Title = styled.h2`
  color: #333;
  margin-bottom: 0.5em;
  font-weight: 600;
`;

const Subtitle = styled.p`
  color: #666;
  margin-top: 0;
  margin-bottom: 2em;
`;

const Form = styled.div`
  display: flex;
  flex-direction: column;
`;

const Input = styled.input`
  padding: 10px;
  margin: 10px 0;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 16px;
`;

const Button = styled.button`
  background-color: #93C572;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  padding: 10px 20px;
  margin-right: 10px;

  &:hover {
    background-color: #698D51;
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const Paragraph = styled.p`
  padding-top: 0px;
  margin: 0 0 20px 0;
  text-align: center;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  line-height: 15px;
`;

const BottomText = styled.p`
  margin-top: 1.5em;
  text-align: right;
`;

const defaultRegistrationDetails = {
    cEaddress: "",
    nLevel: 51,
    cMemword: "",
    cTitle: "Mr",
    cChristname: "",
    cSurname: "",
    cCompanyname: "",
    cCoaddress1: "",
    cCoaddress2: "",
    cCoaddress3: "",
    cCoaddress4: "",
    cCopostcode: "",
    cCotelephone: "",
    cLensmaterial: "crandpoly",
    cLenstype: "svbfvf",
    cDistributor: "-",
    cSideshields: "Optional",
    cSafetyorvdu: "vdu",
    iLearn_of_parmelee: 1,
    cJobtitle: "",
    cEmployees: "-",
    cUseplano: "Yes",
    cMultisites: "No",
    cUsegloves: "No",
    cAccountdescrip: "-",
    password: ""
}

export default function Register() {
    const stepTitles = ["Personal Details", "Account Details", "Company Details"];
    const stepDescriptions = ["Please provide some basic information about yourself", "Create a secure set of credentials for your account", "Please provide some information about the business that you are representing"];

    const [currentStep, setCurrentStep] = useState(0);
    const [registrationDetails, setRegistrationDetails] = useState(defaultRegistrationDetails);

    const [missingFields, setMissingFields] = useState({});

    const [missingPasswordFields, setMissingPasswordFields] = useState({});
    const [passwords, setPasswords] = useState({ password1: '', password2: '' });
    const [passwordErrors, setPasswordErrors] = useState();

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const navigate = useNavigate();

    const titles = [
        { label: 'Mr', value: 'Mr' },
        { label: 'Mrs', value: 'Mrs' },
        { label: 'Miss', value: 'Miss' },
        { label: 'Ms', value: 'Ms' },
        { label: 'Dr', value: 'Dr' }
    ];

    const accountTypes = [
        { label: 'End User', value: 51 },
        { label: 'Distributor', value: 4 },
        { label: 'Optician', value: 9 }
    ];

    const handlePasswordInput = (field, value) => {
        setPasswords(prev => ({ ...prev, [field]: value }));
    };

    const validatePassword = () => {
        setPasswordErrors();
        let newMissingFields = {};
        let isValid = true;
        const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;

        if (!passwords.password1) newMissingFields.password1 = true;
        if (!passwords.password2) newMissingFields.password2 = true;

        for (let key in newMissingFields) {
            if (newMissingFields[key] === true) {
                isValid = false;
                setPasswordErrors("Both password fields must be populated");
                break;
            }
        }

        if (isValid) {
            if (passwords.password1 !== passwords.password2) {
                isValid = false;
                setPasswordErrors("Passwords do not match");
            }
            else {
                if (!passwordRegex.test(passwords.password1)) {
                    isValid = false;
                    setPasswordErrors("Password must be at least 8 characters, include uppercase, lowercase, number, and symbol");
                }
            }
        }

        if (isValid) registrationDetails.password = passwords.password1;

        setMissingPasswordFields(newMissingFields);
        return isValid;
    }

    const validateForm = () => {
        let newMissingFields = {};
        let isValid = true;

        if (!registrationDetails.cTitle && currentStep === 0) newMissingFields.cTitle = true;
        if (!registrationDetails.cChristname && currentStep === 0) newMissingFields.cChristname = true;
        if (!registrationDetails.cSurname && currentStep === 0) newMissingFields.cSurname = true;

        if (!registrationDetails.cEaddress && currentStep === 1) newMissingFields.cEaddress = true;

        if (currentStep === 1) isValid = validatePassword();
        if (!registrationDetails.cMemword && currentStep === 1) newMissingFields.cMemword = true;

        if (!registrationDetails.cCompanyname && currentStep === 2) newMissingFields.cCompanyname = true;
        if (!registrationDetails.cJobtitle && currentStep === 2) newMissingFields.cJobtitle = true;
        if (!registrationDetails.cCoaddress1 && currentStep === 2) newMissingFields.cCoaddress1 = true;
        if (!registrationDetails.cCompanyname && currentStep === 2) newMissingFields.cCompanyname = true;
        if (!registrationDetails.cJobtitle && currentStep === 2) newMissingFields.cJobtitle = true;
        if (!registrationDetails.cCoaddress1 && currentStep === 2) newMissingFields.cCoaddress1 = true;
        if (!registrationDetails.cCoaddress2 && currentStep === 2) newMissingFields.cCoaddress2 = true;
        if (!registrationDetails.cCoaddress4 && currentStep === 2) newMissingFields.cCoaddress4 = true;
        if (!registrationDetails.cCopostcode && currentStep === 2) newMissingFields.cCopostcode = true;
        if (!registrationDetails.cCotelephone && currentStep === 2) newMissingFields.cCotelephone = true;

        for (let key in newMissingFields) {
            if (newMissingFields[key] === true) {
                isValid = false;
                break;
            }
        }

        setMissingFields(newMissingFields);
        return isValid;
    };

    const nextStep = () => {
        if (validateForm()) {
            if (currentStep < stepTitles.length) {
                setCurrentStep(currentStep + 1);
            }
        };
    };

    const prevStep = () => {
        validateForm();

        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    function handleEnteredValues(identifier, value) {
        setRegistrationDetails((prevValues) => ({
            ...prevValues,
            [identifier]: value
        }));
    }

    const handleRegistration = async () => {
        const isValid = validateForm();
    
        if (!isValid) {
          return;
        }
    
        setIsLoading(true);
        try {
          const response = await register(registrationDetails);    
          setError(null);        
          navigate('/login?registered=success');
        } catch (e) {
          setError(e);
          navigate('/login?registered=failed');
        } 
      };

    return (
        <LoginContainer>
            <Container>
                <img src={logo} alt="Logo" />
                <Title>{stepTitles[currentStep]}</Title>
                <Subtitle>{stepDescriptions[currentStep]}</Subtitle>
            </Container>
            <Form>
                {currentStep === 0 && <>
                    <Row>
                        <FieldContainer>
                            <SelectWithLabel
                                label="Title"
                                options={titles}
                                missing={missingFields.cTitle}
                                required={true}
                                value={registrationDetails.cTitle}
                                onChange={(event) => handleEnteredValues('cTitle', event.target.value)} />
                        </FieldContainer>
                    </Row>
                    <Row>
                        <FieldContainer>
                            <TextInputWithLabel
                                label='First name'
                                missing={missingFields.cChristname}
                                required={true}
                                value={registrationDetails.cChristname}
                                onChange={(event) => handleEnteredValues('cChristname', event.target.value)} />
                        </FieldContainer>
                        <FieldContainer>
                            <TextInputWithLabel
                                label='Surname'
                                missing={missingFields.cSurname}
                                required={true}
                                value={registrationDetails.cSurname}
                                onChange={(event) => handleEnteredValues('cSurname', event.target.value)} />
                        </FieldContainer>
                    </Row></>}
                {currentStep === 1 && <>
                    <FieldContainer>
                        <TextInputWithLabel
                            label='Email'
                            missing={missingFields.cEaddress}
                            required={true}
                            value={registrationDetails.cEaddress}
                            onChange={(event) => handleEnteredValues('cEaddress', event.target.value)} />
                    </FieldContainer>
                    <Row>
                        <FieldContainer>
                            <TextInputWithLabel
                                type='password'
                                missing={missingPasswordFields.password1}
                                label='Password'
                                required={true}
                                value={passwords.password1}
                                onChange={e => handlePasswordInput('password1', e.target.value)} />
                        </FieldContainer>
                        <FieldContainer>
                            <TextInputWithLabel
                                type='password'
                                missing={missingPasswordFields.password2}
                                label='Confirm Password'
                                required={true}
                                value={passwords.password2}
                                onChange={e => handlePasswordInput('password2', e.target.value)} />
                        </FieldContainer>
                    </Row>
                    <Row>
                        <FieldContainer>
                            <TextInputWithLabel
                                label='Memorable word'
                                missing={missingFields.cMemword}
                                required={true}
                                value={registrationDetails.cMemword}
                                onChange={(event) => handleEnteredValues('cMemword', event.target.value)} />
                        </FieldContainer>
                    </Row>
                    <Row>
                        <FieldContainer>
                            <SelectWithLabel
                                label="Account Type"
                                options={accountTypes}
                                required={true}
                                value={registrationDetails.nLevel}
                                onChange={(event) => handleEnteredValues('nLevel', parseInt(event.target.value))} />
                        </FieldContainer>
                    </Row>
                    {registrationDetails.nLevel === 51 && <Alert type='info'>The end user account type is the organisation who will be raising orders (EyeTickets) on behalf of their employees.</Alert>}
                    {registrationDetails.nLevel === 4 && <Alert type='info'>A PPE distributor will be an orgnaisation who can manage all their end user accounts and view order history.</Alert>}
                    {registrationDetails.nLevel === 9 && <Alert type='info'>The optician is the optician who will verify the EyeTicket request and approve on appropriate eyewear.</Alert>}
                </>
                }
                {currentStep === 2 && <>
                    <Row>
                        <FieldContainer>
                            <TextInputWithLabel
                                label='Company Name'
                                missing={missingFields.cCompanyname}
                                required={true}
                                value={registrationDetails.cCompanyname}
                                onChange={(event) => handleEnteredValues('cCompanyname', event.target.value)} />
                        </FieldContainer>
                        <FieldContainer>
                            <TextInputWithLabel
                                label='Your Job Title'
                                missing={missingFields.cJobtitle}
                                required={true}
                                value={registrationDetails.cJobtitle}
                                onChange={(event) => handleEnteredValues('cJobtitle', event.target.value)} />
                        </FieldContainer>
                    </Row>
                    <FieldContainer>
                        <TextInputWithLabel
                            label='Address Line 1'
                            missing={missingFields.cCoaddress1}
                            required={true}
                            value={registrationDetails.cCoaddress1}
                            onChange={(event) => handleEnteredValues('cCoaddress1', event.target.value)} />
                    </FieldContainer>
                    <FieldContainer>
                        <TextInputWithLabel
                            label='Address Line 2'
                            missing={missingFields.cCoaddress2}
                            required={true}
                            value={registrationDetails.cCoaddress2}
                            onChange={(event) => handleEnteredValues('cCoaddress2', event.target.value)} />
                    </FieldContainer>
                    <FieldContainer>
                        <TextInputWithLabel
                            label='Address Line 3'
                            missing={missingFields.cCoaddress3}
                            required={false}
                            value={registrationDetails.cCoaddress3}
                            onChange={(event) => handleEnteredValues('cCoaddress3', event.target.value)} />
                    </FieldContainer>
                    <Row>
                        <FieldContainer>
                            <TextInputWithLabel
                                label='County'
                                missing={missingFields.cCoaddress4}
                                required={true}
                                value={registrationDetails.cCoaddress4}
                                onChange={(event) => handleEnteredValues('cCoaddress4', event.target.value)} />
                        </FieldContainer>
                        <FieldContainer>
                            <TextInputWithLabel
                                label='Postcode'
                                missing={missingFields.cCopostcode}
                                required={true}
                                value={registrationDetails.cCopostcode}
                                onChange={(event) => handleEnteredValues('cCopostcode', event.target.value)} />
                        </FieldContainer>
                    </Row>
                    <FieldContainer>
                        <TextInputWithLabel
                            label='Telephone'
                            missing={missingFields.cCotelephone}
                            required={true}
                            value={registrationDetails.cCotelephone}
                            onChange={(event) => handleEnteredValues('cCotelephone', event.target.value)} />
                    </FieldContainer>
                </>}
            </Form>
            <BottomText>
                {
                    (Object.values(missingFields).some(isMissing => isMissing) || passwordErrors) && (
                        passwordErrors ? (
                            <Alert type='warning'>
                                {passwordErrors}
                                {Object.values(missingFields).some(isMissing => isMissing) && " and all required fields indicated by an asterisk * must be populated."}
                            </Alert>
                        ) : (
                            <Alert type='warning'>
                                Please populate all required fields indicated by an asterisk
                                <span style={{ color: '#880808' }}> *</span>
                            </Alert>
                        )
                    )
                }
                {currentStep > 0 && <Button onClick={prevStep}>Back</Button>}
                {currentStep < stepTitles.length - 1 && <Button onClick={nextStep}>Next</Button>}
                {currentStep === stepTitles.length - 1 && <Button 
                onClick={handleRegistration}
                disabled={isLoading}>Submit</Button>}
            </BottomText>
        </LoginContainer >
    );
}
